/* eslint-disable no-param-reassign */
import { format, startOfMonth, startOfWeek } from 'date-fns';
import { getDateRangeByType, intToDate, parseFlexibleDate } from '@/helpers/dateTime';
import { getXAxisFormatType } from '../../helpers/chartXAxisFormatter';
import otpService from '../../services/otpService';

// Latest OTP data / route-list request; responses can come back out of order, and only the latest
// request may write the state.
let latestOtpDataRequest = 0;
let latestRoutesRequest = 0;

/**
 * Helper function to sort arrays alphabetically with Turkish locale support
 * Uses localeCompare with 'tr-TR' locale for proper Turkish character ordering
 * @param {Array} array - Array of objects to sort
 * @param {string} key - Property name to sort by
 * @returns {Array} - Sorted array
 */
function sortAlphabetically(array, key) {
  if (!Array.isArray(array) || array.length === 0) {
    return array;
  }

  return [...array].sort((a, b) => {
    const valueA = a[key] || '';
    const valueB = b[key] || '';
    return valueA.localeCompare(valueB, 'tr-TR');
  });
}

/**
 * Helper function to filter stops based on stopType
 */
function filterStopsByType(stops, stopType) {
  if (!stops || !Array.isArray(stops) || stops.length === 0) {
    return [];
  }

  switch (stopType) {
    case 'first':
      // First stop has stopOrder = 1
      return stops.filter((stop) => stop.stopOrder === 1);
    case 'last': {
      // Last stop has stopOrder = stops.length
      const maxOrder = Math.max(...stops.map((s) => s.stopOrder || 0));
      return stops.filter((stop) => stop.stopOrder === maxOrder);
    }
    case 'all':
    default:
      return stops;
  }
}

/**
 * Helper function to calculate stop-based statistics from a trip
 * Respects stopType filter and returns accurate counts
 *
 * @param {Object} trip - Trip object with stops array
 * @param {string} stopType - Filter type: 'first', 'last', or 'all'
 * @returns {Object} Stop statistics
 */
function calculateTripStopStats(trip, stopType) {
  const stats = {
    totalStops: 0,
    lateStops: 0,
    earlyStops: 0,
    skippedStops: 0,
    onTimeStops: 0,
    lateDelays: [],
    earlyDelays: [],
  };

  // Use trip.totalStops from service data for accurate count (includes skipped stops)
  // Skipped stops are not in the stops array, so we can't rely on array length
  if (stopType === 'all') {
    stats.totalStops = trip.totalStops || 0;
  } else {
    // For 'first' or 'last' stopType, count filtered stops
    const filteredStops = filterStopsByType(trip.stops || [], stopType);
    stats.totalStops = filteredStops.length;
  }

  if (!trip.stops || !Array.isArray(trip.stops) || trip.stops.length === 0) {
    return stats;
  }

  // Filter stops based on stopType
  const filteredStops = filterStopsByType(trip.stops, stopType);

  filteredStops.forEach((stop) => {
    // Count stop types
    if (stop.lateArrival || stop.lateDeparture) {
      stats.lateStops += 1;
      // Collect delay for box plot
      if (stop.arrivalDelay || stop.departureDelay) {
        const maxDelay = Math.max(stop.arrivalDelay || 0, stop.departureDelay || 0);
        const delayMinutes = Math.abs(maxDelay);
        if (delayMinutes > 0) {
          stats.lateDelays.push(delayMinutes);
        }
      }
    }

    if (stop.earlyDeparture) {
      stats.earlyStops += 1;
      // Collect delay for box plot
      if (stop.departureDelay) {
        const delayMinutes = Math.abs(stop.departureDelay);
        if (delayMinutes > 0) {
          stats.earlyDelays.push(delayMinutes);
        }
      }
    }

    if (stop.otp) {
      stats.onTimeStops += 1;
    }

    if (stop.skipped) {
      stats.skippedStops += 1;
    }
  });

  return stats;
}

/**
 * Helper function to filter trips by direction
 *
 * @param {Array} trips - Array of trip objects
 * @param {string} direction - Direction filter: 'OUTBOUND', 'INBOUND', 'LOOP', 'all', or null/undefined for all
 * @returns {Array} Filtered trips array
 */
function filterTripsByDirection(trips, direction) {
  if (!direction || direction === 'all') {
    return trips;
  }
  // Case-insensitive comparison since filter sends lowercase but trip.direction is uppercase
  return trips.filter((trip) => trip.direction?.toUpperCase() === direction.toUpperCase());
}

/**
 * Helper function to create initial aggregated stats structure
 * Used by both aggregateRouteStats and aggregateDateStats for consistent initialization
 *
 * @param {string} keyType - Type of key: 'route' or 'date'
 * @param {Object} trip - Trip object containing the key value
 * @returns {Object} Initial stats structure
 */
function createAggregatedStatsStructure(keyType, trip) {
  const baseStats = {
    trips: [],
    totalStops: 0,
    lateStops: 0,
    earlyStops: 0,
    skippedStops: 0,
    onTimeStops: 0,
    leftBehind: 0,
    onTimeTrips: 0,
    lateTrips: 0,
    earlyTrips: 0,
  };

  if (keyType === 'route') {
    return {
      routeId: trip.routeId,
      routeName: trip.route,
      ...baseStats,
    };
  }

  if (keyType === 'date') {
    return {
      date: trip.date,
      dayOfWeek: trip.dayOfWeek?.trim(),
      ...baseStats,
      lateDelays: [],
      earlyDelays: [],
      leftBehindEvents: [],
    };
  }

  return baseStats;
}

/**
 * Helper function to calculate percentage-based stats from accumulated data
 * Returns consistent stats object used by both aggregateRouteStats and aggregateDateStats
 *
 * @param {Object} aggregatedData - Accumulated data with stop counts
 * @returns {Object} Stats object with percentages and counts
 */
function calculateStatsPercentages(aggregatedData) {
  const { totalStops, lateStops, earlyStops, skippedStops, onTimeStops } = aggregatedData;
  return {
    late: totalStops > 0 ? Math.round((lateStops / totalStops) * 100) : 0,
    onTime: totalStops > 0 ? Math.round((onTimeStops / totalStops) * 100) : 0,
    early: totalStops > 0 ? Math.round((earlyStops / totalStops) * 100) : 0,
    skipped: totalStops > 0 ? Math.round((skippedStops / totalStops) * 100) : 0,
    totalStops,
    totalTrips: aggregatedData.trips.length,
    lateCount: lateStops,
    earlyCount: earlyStops,
    skippedCount: skippedStops,
    leftBehind: aggregatedData.leftBehind,
    // Include trip-level counts for OTP chart
    onTimeTrips: aggregatedData.onTimeTrips || 0,
    lateTrips: aggregatedData.lateTrips || 0,
    earlyTrips: aggregatedData.earlyTrips || 0,
  };
}

/**
 * Helper function to aggregate stop-level statistics for a route
 * Processes all trips in a route and groups statistics by stop
 *
 * @param {Array} trips - Array of trip objects for the route
 * @param {string} stopType - Filter type: 'first', 'last', or 'all'
 * @returns {Array} Array of stop statistics objects
 */
function aggregateStopsByRoute(trips, stopType) {
  const stopMap = new Map();

  trips.forEach((trip) => {
    if (trip.stops && Array.isArray(trip.stops)) {
      const filteredStops = filterStopsByType(trip.stops, stopType);

      filteredStops.forEach((stop) => {
        const stopKey = stop.stopId;

        if (!stopMap.has(stopKey)) {
          stopMap.set(stopKey, {
            stopId: stop.stopId,
            stopName: stop.stopName,
            totalStops: 0,
            lateStops: 0,
            earlyStops: 0,
            onTimeStops: 0,
            skippedStops: 0,
            leftBehind: 0,
          });
        }

        const stopData = stopMap.get(stopKey);
        stopData.totalStops += 1;

        if (stop.lateArrival || stop.lateDeparture) {
          stopData.lateStops += 1;
        }
        if (stop.earlyDeparture) {
          stopData.earlyStops += 1;
        }
        if (stop.otp) {
          stopData.onTimeStops += 1;
        }
        if (stop.skipped) {
          stopData.skippedStops += 1;
        }
        if (stop.ridersLeftBehind && stop.ridersLeftBehind !== 'NA') {
          stopData.leftBehind += parseInt(stop.ridersLeftBehind, 10) || 0;
        }
      });
    }
  });

  // Convert to array and calculate percentages
  return Array.from(stopMap.values()).map((stopData) => ({
    stopId: stopData.stopId,
    stopName: stopData.stopName,
    stats: {
      late: stopData.totalStops > 0 ? Math.round((stopData.lateStops / stopData.totalStops) * 100) : 0,
      onTime: stopData.totalStops > 0 ? Math.round((stopData.onTimeStops / stopData.totalStops) * 100) : 0,
      early: stopData.totalStops > 0 ? Math.round((stopData.earlyStops / stopData.totalStops) * 100) : 0,
      skipped: stopData.totalStops > 0 ? Math.round((stopData.skippedStops / stopData.totalStops) * 100) : 0,
      totalStops: stopData.totalStops,
      lateCount: stopData.lateStops,
      earlyCount: stopData.earlyStops,
      skippedCount: stopData.skippedStops,
      leftBehind: stopData.leftBehind,
    },
  }));
}

/**
 * Helper function to process a trip and accumulate statistics into aggregated data
 * Reduces code duplication between aggregateRouteStats and aggregateDateStats
 *
 * @param {Object} trip - Trip object to process
 * @param {Object} aggregatedData - Accumulated data object to update
 * @param {Object} filters - Filter object containing stopType and threshold
 */
function processTripAndAccumulateStats(trip, aggregatedData, filters) {
  aggregatedData.trips.push(trip);

  // Calculate stop statistics using helper function (respects stopType filter)
  const stopStats = calculateTripStopStats(trip, filters.stopType);
  aggregatedData.totalStops += stopStats.totalStops;
  aggregatedData.lateStops += stopStats.lateStops;
  aggregatedData.earlyStops += stopStats.earlyStops;
  aggregatedData.skippedStops += stopStats.skippedStops;
  aggregatedData.onTimeStops += stopStats.onTimeStops;

  // Collect delays for box plot calculations (if arrays exist)
  if (aggregatedData.lateDelays) {
    aggregatedData.lateDelays.push(...stopStats.lateDelays);
  }
  if (aggregatedData.earlyDelays) {
    aggregatedData.earlyDelays.push(...stopStats.earlyDelays);
  }

  // Handle riders left behind
  if (trip.ridersLeftBehind && trip.ridersLeftBehind !== 'NA') {
    const leftBehindCount = parseInt(trip.ridersLeftBehind, 10) || 0;
    aggregatedData.leftBehind += leftBehindCount;

    // Track left behind events (if array exists), one per stop that actually left riders
    // behind. The trip's own total is the sum of those stops (stops not sent to the driver
    // excluded), so the events add up to it — but they used to be reported against
    // `stops[0]`, naming the trip's first stop whatever stop the riders were waiting at.
    if (aggregatedData.leftBehindEvents && leftBehindCount > 0) {
      const stopsLeftBehind = (trip.stops || []).filter((stop) => !stop.notSentToDriver && (stop.leftBehind || 0) > 0);
      if (stopsLeftBehind.length > 0) {
        stopsLeftBehind.forEach((stop) => {
          aggregatedData.leftBehindEvents.push({
            count: stop.leftBehind,
            routeName: trip.route,
            stopName: stop.stopName,
          });
        });
      } else {
        // No per-stop breakdown in the payload: keep the trip's own figure.
        aggregatedData.leftBehindEvents.push({
          count: leftBehindCount,
          routeName: trip.route,
          stopName: 'Unknown',
        });
      }
    }
  }

  // Classify trip based on OTP percentage
  const otpPercent = parseFloat(trip.otpPercentage) || 0;
  if (otpPercent >= filters.threshold) {
    aggregatedData.onTimeTrips += 1;
  } else if (stopStats.lateStops > 0) {
    aggregatedData.lateTrips += 1;
  } else if (stopStats.earlyStops > 0) {
    aggregatedData.earlyTrips += 1;
  }
}

/**
 * Calculate box plot statistics from an array of numbers
 */
function calculateBoxPlot(values) {
  if (!values || values.length === 0) {
    return {
      min: 0,
      q1: 0,
      median: 0,
      q3: 0,
      max: 0,
      outliers: [],
      count: 0,
    };
  }

  const sorted = [...values].sort((a, b) => a - b);
  const { length } = sorted;

  const q1Index = Math.floor(length * 0.25);
  const medianIndex = Math.floor(length * 0.5);
  const q3Index = Math.floor(length * 0.75);

  const q1 = sorted[q1Index];
  const median = sorted[medianIndex];
  const q3 = sorted[q3Index];
  const iqr = q3 - q1;

  const lowerBound = q1 - 1.5 * iqr;
  const upperBound = q3 + 1.5 * iqr;

  const outliers = sorted.filter((v) => v < lowerBound || v > upperBound);

  // min max decided to be set wo outliers as production team requested
  // const nonOutliers = sorted.filter((v) => v >= lowerBound && v <= upperBound);

  return {
    min: sorted[0],
    q1,
    median,
    q3,
    max: sorted[length - 1],
    outliers,
    count: length,
  };
}

/**
 * Normalize any date value (YYYYMMDD int/string, ISO string, Date) to a YYYYMMDD integer key.
 * Used to match trip dates against the selected date range regardless of the source format.
 *
 * @param {string|number|Date} dateValue - Date in any format parseFlexibleDate understands
 * @returns {number|null} YYYYMMDD integer key, or null if the date cannot be parsed
 */
function normalizeToDateKey(dateValue) {
  const date = parseFlexibleDate(dateValue);
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return parseInt(`${y}${m}${d}`, 10);
}

/**
 * Generate an array of YYYYMMDD integer keys for every day in the given range (inclusive).
 *
 * @param {number} startDay - Start day in YYYYMMDD format
 * @param {number} endDay - End day in YYYYMMDD format
 * @returns {number[]} Array of YYYYMMDD integer keys (empty if inputs are invalid)
 */
function generateDateRangeKeys(startDay, endDay) {
  if (!startDay || !endDay) return [];
  const start = intToDate(startDay);
  const end = intToDate(endDay);
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];

  const keys = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endMs = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
  // The cursor always advances by one day and is bounded by endMs, so the loop
  // is guaranteed to terminate without an artificial iteration cap (which would
  // silently truncate valid ranges longer than the cap).
  while (cursor.getTime() <= endMs) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, '0');
    const d = String(cursor.getDate()).padStart(2, '0');
    keys.push(parseInt(`${y}${m}${d}`, 10));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
}

/**
 * The period the by-date chart buckets trips into, following the same rules as the other
 * dashboard charts: hours for a single day, days up to a month, weeks up to six months and
 * months beyond that.
 *
 * @param {Object} dateRange - Selected range ({ type, startDay, endDay })
 * @returns {string} 'hourly' | 'daily' | 'weekly' | 'monthly'
 */
function resolveDatePeriod(dateRange) {
  if (!dateRange?.startDay || !dateRange?.endDay) return 'daily';
  return getXAxisFormatType(intToDate(dateRange.startDay), intToDate(dateRange.endDay), dateRange.type);
}

/**
 * Hour (0-23) a trip is filed under on the single-day view, taken from its planned first-stop
 * arrival — the payload's only trip-level time. The backend sends it as an unpadded HHmm string
 * ("640" is 06:40, "1443" is 14:43) and "-" or null when the trip has none. Stops carry only
 * deviations, not planned times, so a trip counts in the hour it starts in as a whole.
 *
 * @param {Object} trip - Trip from the OTP payload
 * @returns {number|null} Hour of day, or null when the trip has no usable planned time
 */
function tripPlannedHour(trip) {
  const { plannedFirstStopArrival } = trip;
  if (plannedFirstStopArrival === null || plannedFirstStopArrival === undefined || plannedFirstStopArrival === '') {
    return null;
  }
  const time = Number(plannedFirstStopArrival);
  if (!Number.isFinite(time) || time < 0) return null;
  return Math.floor(time / 100) % 24;
}

/**
 * YYYYMMDD key of the first day of the week (Monday) or month a day falls in; the day itself
 * for the other periods.
 *
 * @param {number} dayKey - YYYYMMDD integer key
 * @param {string} period - Period from resolveDatePeriod
 * @returns {number} YYYYMMDD integer key
 */
function periodStartKey(dayKey, period) {
  if (period === 'weekly') return normalizeToDateKey(startOfWeek(intToDate(dayKey), { weekStartsOn: 1 }));
  if (period === 'monthly') return normalizeToDateKey(startOfMonth(intToDate(dayKey)));
  return dayKey;
}

/**
 * Key of the bucket a trip falls in, matching the keys generateDateBuckets hands out.
 *
 * @param {Object} trip - Trip from the OTP payload
 * @param {string} period - Period from resolveDatePeriod
 * @returns {string|number|null} Bucket key, or null when the trip cannot be placed
 */
function tripBucketKey(trip, period) {
  const dayKey = normalizeToDateKey(trip.date);
  if (!dayKey) return null;
  if (period === 'hourly') {
    const hour = tripPlannedHour(trip);
    return hour === null ? null : `${dayKey}-${String(hour).padStart(2, '0')}`;
  }
  return periodStartKey(dayKey, period);
}

/**
 * Every bucket of the selected range, in order, so periods without trips still take a place on
 * the x-axis. A bucket carries:
 * - key: the category the chart, tooltips and left-behind discs match on
 * - date: YYYYMMDD of the day, week (Monday) or month the bucket stands for
 * - hour: hour of day on the hourly view, null otherwise
 * - start/end: YYYYMMDD of the first and last selected day inside the bucket, so a week or
 *   month cut by the range edges reads as the days it actually covers
 *
 * @param {Object} dateRange - Selected range ({ startDay, endDay })
 * @param {string} period - Period from resolveDatePeriod
 * @returns {Object[]} Buckets
 */
function generateDateBuckets(dateRange, period) {
  const dayKeys = generateDateRangeKeys(dateRange?.startDay, dateRange?.endDay);

  if (period === 'hourly') {
    return dayKeys.flatMap((day) =>
      Array.from({ length: 24 }, (_, hour) => ({
        key: `${day}-${String(hour).padStart(2, '0')}`,
        date: day,
        hour,
        start: day,
        end: day,
      })),
    );
  }

  const buckets = new Map();
  dayKeys.forEach((day) => {
    const key = periodStartKey(day, period);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.end = day;
    } else {
      buckets.set(key, { key, date: key, hour: null, start: day, end: day });
    }
  });
  return [...buckets.values()];
}

/**
 * The bucket identity every by-date chart series carries, so the chart, its tooltip and the
 * left-behind discs all match on the same key and label it the same way.
 *
 * @param {Object} dateData - Entry of state.dateStats
 * @returns {Object} { key, date, hour, period, start, end }
 */
function bucketFields({ key, date, hour, period, start, end }) {
  return { key, date, hour, period, start, end };
}

/**
 * Create an empty stat entry for a bucket with no trips.
 * Box plot values are null so the chart renders a gap instead of a zero box,
 * and the OTP percentage is null so the line breaks across empty buckets.
 *
 * @param {Object} bucket - Bucket from generateDateBuckets
 * @param {string} period - Period from resolveDatePeriod
 * @returns {Object} Empty date stat entry
 */
function createEmptyDateStat(bucket, period) {
  return {
    ...bucket,
    period,
    dayOfWeek: null,
    isEmpty: true,
    stats: {
      late: 0,
      onTime: 0,
      early: 0,
      skipped: 0,
      totalStops: 0,
      totalTrips: 0,
      lateCount: 0,
      earlyCount: 0,
      skippedCount: 0,
      leftBehind: 0,
      onTimeTrips: 0,
      lateTrips: 0,
      earlyTrips: 0,
    },
    lateBoxPlot: { min: null, q1: null, median: null, q3: null, max: null, outliers: [], count: 0 },
    earlyBoxPlot: { min: null, q1: null, median: null, q3: null, max: null, outliers: [], count: 0 },
    leftBehindEvents: [],
  };
}

export default {
  namespaced: true,

  state: {
    rawTripData: [],
    routeStats: [],
    dateStats: [],
    availableRoutes: [],
    // { startDay, endDay } availableRoutes were loaded for. They outlive the dashboard, so after a date
    // change they belong to the previous range until the new list arrives.
    availableRoutesRange: null,
    availableDrivers: [],
    filters: {
      dateRange: getDateRangeByType('lastSevenDays'),
      threshold: 5,
      routeIds: [],
      driverIds: [],
      direction: 'all',
      stopType: 'all',
    },
    loading: false,
    error: null,
  },

  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading;
    },

    SET_ERROR(state, error) {
      state.error = error;
    },

    SET_RAW_DATA(state, data) {
      state.rawTripData = Array.isArray(data) ? data : [];
    },

    SET_FILTERS(state, filters) {
      state.filters = { ...state.filters, ...filters };
    },

    UPDATE_DATE_RANGE(state, dateRange) {
      // dateRange should be an object: { type, fromDate, toDate }
      state.filters.dateRange = dateRange;
    },

    UPDATE_ROUTES(state, routes) {
      state.filters.routeIds = Array.isArray(routes) ? routes : [routes];
    },

    UPDATE_DRIVERS(state, drivers) {
      state.filters.driverIds = Array.isArray(drivers) ? drivers : [drivers];
    },

    UPDATE_DIRECTION(state, direction) {
      state.filters.direction = direction;
    },

    UPDATE_STOP_TYPE(state, stopType) {
      state.filters.stopType = stopType;
    },

    UPDATE_THRESHOLD(state, threshold) {
      state.filters.threshold = threshold;
    },

    RESET_FILTERS(state, defaultThreshold = 5) {
      state.filters = {
        dateRange: getDateRangeByType('yesterday'),
        threshold: defaultThreshold,
        routeIds: [],
        driverIds: [],
        direction: 'all',
        stopType: 'all',
      };
    },

    SET_ROUTE_STATS(state, stats) {
      // Sort route stats alphabetically by routeName using Turkish locale
      state.routeStats = sortAlphabetically(stats, 'routeName');
    },

    SET_DATE_STATS(state, stats) {
      state.dateStats = stats;
    },

    SET_AVAILABLE_ROUTES(state, routes) {
      // Sort routes alphabetically by name using Turkish locale
      state.availableRoutes = sortAlphabetically(routes, 'name');
    },

    SET_AVAILABLE_ROUTES_RANGE(state, range) {
      state.availableRoutesRange = range;
    },

    SET_AVAILABLE_DRIVERS(state, drivers) {
      // Sort drivers alphabetically by name using Turkish locale
      state.availableDrivers = sortAlphabetically(drivers, 'name');
    },

    // Also invalidates a data request still in flight, so a response for the cleared view can't land afterwards.
    CLEAR_DATA(state) {
      latestOtpDataRequest += 1;
      state.rawTripData = [];
      state.routeStats = [];
      state.dateStats = [];
      state.loading = false;
      state.error = null;
    },
  },

  actions: {
    async fetchRoutesAndDrivers({ commit }, params) {
      latestRoutesRequest += 1;
      const request = latestRoutesRequest;
      try {
        const { startDay, endDay } = params;
        const data = await otpService.getRoutesDriversByDate({ startDay, endDay });

        if (data && request === latestRoutesRequest) {
          commit('SET_AVAILABLE_ROUTES_RANGE', { startDay, endDay });
          commit('SET_AVAILABLE_ROUTES', data.routes || []);
          commit('SET_AVAILABLE_DRIVERS', data.drivers || []);
        }

        return data;
      } catch (error) {
        console.error('Error fetching routes and drivers:', error);
        throw error;
      }
    },
    async fetchOtpData({ commit, dispatch }, params) {
      latestOtpDataRequest += 1;
      const request = latestOtpDataRequest;
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      try {
        // Fetch data from API
        const data = await otpService.getOtpDashboardData(params);
        // A newer request is on its way; it sets the data and clears the loading state.
        if (request !== latestOtpDataRequest) return data;

        commit('SET_RAW_DATA', data);

        await dispatch('aggregateRouteStats');
        await dispatch('aggregateDateStats');

        commit('SET_LOADING', false);
        return data;
      } catch (error) {
        if (request === latestOtpDataRequest) {
          commit('SET_ERROR', error.message || 'Failed to fetch OTP data');
          commit('SET_LOADING', false);
        }
        throw error;
      }
    },

    async reAggregateData({ dispatch }) {
      // Re-run all aggregations on existing rawTripData
      // Used when filters like stopType or direction change without requiring new API data
      await dispatch('aggregateDateStats');
      await dispatch('aggregateRouteStats');
    },

    aggregateRouteStats({ state, commit }) {
      const { rawTripData, filters } = state;

      if (!rawTripData || rawTripData.length === 0) {
        commit('SET_ROUTE_STATS', []);
        return;
      }

      // Filter trips by direction if direction filter is set
      const filteredTrips = filterTripsByDirection(rawTripData, filters.direction);

      if (filteredTrips.length === 0) {
        commit('SET_ROUTE_STATS', []);
        return;
      }

      const routeMap = new Map();

      filteredTrips.forEach((trip) => {
        const routeKey = trip.route;

        if (!routeMap.has(routeKey)) {
          routeMap.set(routeKey, createAggregatedStatsStructure('route', trip));
        } else {
          // Ensure routeId is set even for subsequent trips (in case first trip was missing it)
          const routeData = routeMap.get(routeKey);
          if (!routeData.routeId && trip.routeId) {
            routeData.routeId = trip.routeId;
          }
        }

        const routeData = routeMap.get(routeKey);

        // Process trip and accumulate statistics using helper function
        processTripAndAccumulateStats(trip, routeData, filters);
      });

      const routeStats = Array.from(routeMap.values()).map((routeData) => ({
        routeId: routeData.routeId,
        routeName: routeData.routeName,
        stats: {
          ...calculateStatsPercentages(routeData),
          dateRange: {
            start: filters.startDay
              ? format(new Date(String(filters.startDay).replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')), 'yyyy-MM-dd')
              : null,
            end: filters.endDay
              ? format(new Date(String(filters.endDay).replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')), 'yyyy-MM-dd')
              : null,
          },
        },
        stops: aggregateStopsByRoute(routeData.trips, filters.stopType),
      }));

      commit('SET_ROUTE_STATS', routeStats);
    },

    aggregateDateStats({ state, commit }) {
      const { rawTripData, filters } = state;

      // Filter trips by direction if direction filter is set
      const filteredTrips = filterTripsByDirection(rawTripData, filters.direction);

      // Bucket the selected range by the period its length calls for, and emit every bucket
      // so periods without trips appear on the x-axis (the chart renders gaps for them
      // instead of skipping them).
      const { dateRange } = filters;
      const period = resolveDatePeriod(dateRange);
      const buckets = generateDateBuckets(dateRange, period);

      if (!rawTripData || rawTripData.length === 0 || filteredTrips.length === 0) {
        commit(
          'SET_DATE_STATS',
          buckets.map((bucket) => createEmptyDateStat(bucket, period)),
        );
        return;
      }

      // Trip dates are normalized to a YYYYMMDD integer before bucketing, so every
      // representation of the same calendar day (e.g. 20260813 vs "2026-08-13") lands in
      // the same bucket. Stop counts and delays are accumulated rather than averaged, so
      // a week's or month's OTP is weighted by its stops like a day's is.
      const bucketMap = new Map();

      filteredTrips.forEach((trip) => {
        const key = tripBucketKey(trip, period);
        // Only happens on the hourly view, for a trip without a planned time.
        if (key === null) return;

        if (!bucketMap.has(key)) {
          bucketMap.set(key, createAggregatedStatsStructure('date', trip));
        }

        processTripAndAccumulateStats(trip, bucketMap.get(key), filters);
      });

      const dateStats = buckets.map((bucket) => {
        const dateData = bucketMap.get(bucket.key);

        if (!dateData) {
          return createEmptyDateStat(bucket, period);
        }

        // Calculate box plot statistics
        const lateBoxPlot = calculateBoxPlot(dateData.lateDelays);
        const earlyBoxPlot = calculateBoxPlot(dateData.earlyDelays);

        return {
          ...bucket,
          period,
          // A day's weekday is still meaningful on the hourly and daily views; a week or
          // month spans several.
          dayOfWeek: period === 'hourly' || period === 'daily' ? dateData.dayOfWeek : null,
          stats: calculateStatsPercentages(dateData),
          lateBoxPlot,
          earlyBoxPlot,
          leftBehindEvents: dateData.leftBehindEvents,
        };
      });

      commit('SET_DATE_STATS', dateStats);
    },

    clearData({ commit }) {
      commit('CLEAR_DATA');
    },
  },

  getters: {
    routeStats: (state) => state.routeStats,
    dateStats: (state) => state.dateStats,
    rawTripData: (state) => state.rawTripData,
    availableRoutes: (state) => state.availableRoutes,
    availableRoutesRange: (state) => state.availableRoutesRange,
    availableDrivers: (state) => state.availableDrivers,

    // Filtered drivers based on selected routes
    filteredAvailableDrivers: (state) => {
      const { routeIds } = state.filters;
      const { availableRoutes, availableDrivers } = state;

      // If no routes selected, return all drivers
      if (!routeIds || routeIds.length === 0) {
        return availableDrivers;
      }

      // Create a Map for O(1) route lookups to avoid O(n²) complexity
      const routeMap = new Map(availableRoutes.map((r) => [r.id, r]));

      // Collect all driverIds from selected routes
      const selectedRouteDriverIds = new Set();
      routeIds.forEach((routeId) => {
        // Handle ring groups where routeId.id is an array of route IDs
        if (typeof routeId === 'object' && routeId.driverIds && Array.isArray(routeId.driverIds)) {
          // For ring groups, use driverIds directly from the selected route object
          routeId.driverIds.forEach((driverId) => selectedRouteDriverIds.add(driverId));
        } else {
          // Find the route object (routeId might be an object or just an ID)
          const routeIdValue = typeof routeId === 'object' ? routeId.id : routeId;
          const route = routeMap.get(routeIdValue);

          if (route && route.driverIds && Array.isArray(route.driverIds)) {
            route.driverIds.forEach((driverId) => selectedRouteDriverIds.add(driverId));
          }
        }
      });

      // Filter drivers to only include those in the selected routes
      return availableDrivers.filter((driver) => {
        const driverId = typeof driver === 'object' ? driver.id : driver;
        return selectedRouteDriverIds.has(driverId);
      });
    },

    loading: (state) => state.loading,
    error: (state) => state.error,
    filters: (state) => state.filters,
    hasData: (state) => state.rawTripData.length > 0,

    // Chart-ready data for OtpByDate component.
    // Empty days (no trips) emit null box-plot/percentage values so Highcharts
    // renders gaps instead of zero boxes/points, while the date is still present
    // as an x-axis category.
    lateArrivalsChartData: (state) =>
      state.dateStats.map((dateData) => {
        if (dateData.isEmpty) {
          return {
            ...bucketFields(dateData),
            min: null,
            q1: null,
            median: null,
            q3: null,
            max: null,
            outliers: [],
            count: 0,
          };
        }
        return {
          ...bucketFields(dateData),
          min: dateData.lateBoxPlot?.min || 0,
          q1: dateData.lateBoxPlot?.q1 || 0,
          median: dateData.lateBoxPlot?.median || 0,
          q3: dateData.lateBoxPlot?.q3 || 0,
          max: dateData.lateBoxPlot?.max || 0,
          outliers: dateData.lateBoxPlot?.outliers || [],
          count: dateData.lateBoxPlot?.count || 0,
        };
      }),

    earlyDeparturesChartData: (state) =>
      state.dateStats.map((dateData) => {
        if (dateData.isEmpty) {
          return {
            ...bucketFields(dateData),
            min: null,
            q1: null,
            median: null,
            q3: null,
            max: null,
            outliers: [],
            count: 0,
          };
        }
        return {
          ...bucketFields(dateData),
          min: dateData.earlyBoxPlot?.min || 0,
          q1: dateData.earlyBoxPlot?.q1 || 0,
          median: dateData.earlyBoxPlot?.median || 0,
          q3: dateData.earlyBoxPlot?.q3 || 0,
          max: dateData.earlyBoxPlot?.max || 0,
          outliers: dateData.earlyBoxPlot?.outliers || [],
          count: dateData.earlyBoxPlot?.count || 0,
        };
      }),

    otpPercentagesChartData: (state) =>
      state.dateStats.map((dateData) => ({
        ...bucketFields(dateData),
        percentage: dateData.isEmpty ? null : dateData.stats.onTime,
        onTimeCount: dateData.stats.onTimeTrips || 0,
        totalCount: dateData.stats.totalTrips,
      })),

    leftBehindChartData: (state) => {
      const leftBehindData = [];
      let position = 0;

      state.dateStats.forEach((dateData) => {
        if (dateData.leftBehindEvents && dateData.leftBehindEvents.length > 0) {
          dateData.leftBehindEvents.forEach((event) => {
            leftBehindData.push({
              ...bucketFields(dateData),
              count: event.count,
              position,
              routeName: event.routeName,
              stopName: event.stopName,
            });
            position += 80; // Increment position for visual spacing
          });
        }
      });

      return leftBehindData;
    },

    /**
     * Get combined stops for a route by stopName
     * Combines stops with the same name (case-insensitive) across a route's stop list
     * - Counts (lateCount, earlyCount, skippedCount, leftBehind, totalStops): summed
     * - Percentages (late, onTime, early, skipped): weighted average by totalStops
     */
    getCombinedStopsForRoute: (state) => (routeId) => {
      const route = state.routeStats.find((r) => r.routeId === routeId || r.routeName === routeId);

      if (!route || !route.stops) return [];

      const nameGroups = new Map();
      route.stops.forEach((stop) => {
        const nameKey = (stop.stopName || '').toLowerCase().trim();
        if (!nameKey) return;

        const { stats } = stop;
        const weight = stats?.totalStops || 1;
        const existing = nameGroups.get(nameKey);

        if (existing) {
          const prevWeight = existing.stats.totalStops;
          const newWeight = prevWeight + weight;
          // Weighted average for percentages
          existing.stats.late = Math.round(
            (existing.stats.late * prevWeight + (stats?.late || 0) * weight) / newWeight,
          );
          existing.stats.onTime = Math.round(
            (existing.stats.onTime * prevWeight + (stats?.onTime || 0) * weight) / newWeight,
          );
          existing.stats.early = Math.round(
            (existing.stats.early * prevWeight + (stats?.early || 0) * weight) / newWeight,
          );
          existing.stats.skipped = Math.round(
            (existing.stats.skipped * prevWeight + (stats?.skipped || 0) * weight) / newWeight,
          );
          // Sum counts
          existing.stats.totalStops += weight;
          existing.stats.lateCount += stats?.lateCount || 0;
          existing.stats.earlyCount += stats?.earlyCount || 0;
          existing.stats.skippedCount += stats?.skippedCount || 0;
          existing.stats.leftBehind += stats?.leftBehind || 0;
        } else {
          nameGroups.set(nameKey, {
            id: stop.stopId,
            name: stop.stopName,
            stats: {
              late: stats?.late || 0,
              onTime: stats?.onTime || 0,
              early: stats?.early || 0,
              skipped: stats?.skipped || 0,
              totalStops: weight,
              lateCount: stats?.lateCount || 0,
              earlyCount: stats?.earlyCount || 0,
              skippedCount: stats?.skippedCount || 0,
              leftBehind: stats?.leftBehind || 0,
            },
          });
        }
      });

      return Array.from(nameGroups.values());
    },
  },
};

/* eslint-enable no-param-reassign */
